import React, { useState } from 'react';
import { ITableRow } from 'ui/Table/types';

export interface IExpandableRowData {
  id: string;
  title: string;
  row: ITableRow;
  className?: string;
}

interface IExpandableTableProps {
  data: IExpandableRowData[];
  className?: string;
}

export const ExpandableTable: React.FC<IExpandableTableProps> = ({ data, className = '' }) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (rowId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  const renderExpandableRow = (item: IExpandableRowData) => {
    const isExpanded = expandedRows.has(item.id);
    const visibleCells = item.row.cells.filter(cell => !cell.isHidden);
    const hasContent = visibleCells.length > 0;

    return (
      <div key={item.id} className={item.className || ''}>
        {/* Expandable Header */}
        <div
          className="flex items-center py-2 px-3 cursor-pointer border-b border-gray-20 bg-white"
          onClick={() => hasContent && toggleRow(item.id)}
        >
          <div className="flex items-center flex-1">
            {hasContent && (
              <>
                {isExpanded ? (
                  <svg
                    className="w-4 h-4 mr-2 stroke-brown-prime"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4 mr-2 stroke-brown-prime"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                )}
              </>
            )}
            <span className="font-medium text-brown-prime text-15px">{item.title}</span>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && hasContent && (
          <div className="bg-white-hard border-b border-gray-20">
            <div className="p-3 pl-0 space-y-2 ml-9">
              {visibleCells.map(cell => (
                <div key={cell.id} className={`grid grid-cols-3 gap-4 text-sm ${cell.className || ''}`}>
                  <div className="text-13px text-gray-100 font-medium">{cell.title || ''}</div>
                  <div className={`text-black text-15px col-span-2 ${cell.contentClassName || ''}`}>
                    {cell.renderValue ? cell.renderValue(isExpanded) : cell.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`bg-white border border-gray-20 overflow-hidden font-pt-sans w-full ${className}`}>
      {data.map(item => renderExpandableRow(item))}
    </div>
  );
};
